import { UserRound, ExternalLink } from "lucide-react";

/* Display components for an event's featured hosts & guests. */

function isHostRole(role) {
  return /host/i.test(role || "");
}

function Avatar({ photo, name, className = "h-16 w-16" }) {
  if (photo) {
    return <img src={photo} alt={name || "person"} className={`${className} rounded-full border border-border object-cover`} />;
  }
  return (
    <div className={`${className} flex items-center justify-center rounded-full border border-border bg-secondary text-muted-foreground`}>
      {name ? <span className="text-lg font-semibold">{name.trim().charAt(0).toUpperCase()}</span> : <UserRound className="h-6 w-6" />}
    </div>
  );
}

function PersonCard({ p }) {
  return (
    <div className="flex gap-4 rounded-xl border border-border bg-card p-4" data-testid={`event-person-card-${p.id || p.name}`}>
      <Avatar photo={p.photo} name={p.name} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-foreground">{p.name}</h3>
          {p.role && (
            <span className="rounded-full border border-[rgba(201,119,46,0.4)] bg-[rgba(201,119,46,0.12)] px-2 py-0.5 text-[11px] font-medium text-[#8A4A18] dark:text-[#E6A463]">
              {p.role}
            </span>
          )}
        </div>
        {p.title && <p className="mt-0.5 text-sm text-muted-foreground">{p.title}</p>}
        {p.bio && <p className="mt-2 text-sm leading-relaxed text-foreground/90">{p.bio}</p>}
        {p.link && (
          <a
            href={p.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            data-testid={`event-person-link-${p.id || p.name}`}
          >
            Learn more <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

export function EventPeople({ people, title = "Hosts & Guests" }) {
  if (!Array.isArray(people) || people.length === 0) return null;
  return (
    <section className="space-y-4" data-testid="event-people">
      <h2 className="font-serif text-xl font-semibold text-foreground sm:text-2xl">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {people.map((p, i) => <PersonCard key={p.id || i} p={p} />)}
      </div>
    </section>
  );
}

function buildSummary(people) {
  const hosts = people.filter((p) => isHostRole(p.role) && p.name);
  const guests = people.filter((p) => !isHostRole(p.role) && p.name);
  const parts = [];
  if (hosts.length) parts.push(`Hosted by ${hosts.slice(0, 2).map((h) => h.name).join(" & ")}`);
  if (guests.length) {
    const first = guests[0];
    const extra = guests.length - 1;
    parts.push(`with ${first.name}${extra > 0 ? ` +${extra}` : ""}`);
  }
  return parts.join(" · ");
}

export function EventPeopleStrip({ people, max = 4, className = "" }) {
  if (!Array.isArray(people) || people.length === 0) return null;
  const named = people.filter((p) => p.name);
  if (named.length === 0) return null;
  const shown = named.slice(0, max);
  const extra = named.length - shown.length;
  const summary = buildSummary(named);
  return (
    <div className={`flex items-center gap-2.5 ${className}`} data-testid="event-people-strip">
      <div className="flex -space-x-2">
        {shown.map((p, i) => (
          <span key={p.id || i} className="ring-2 ring-card rounded-full">
            <Avatar photo={p.photo} name={p.name} className="h-7 w-7" />
          </span>
        ))}
        {extra > 0 && (
          <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-secondary text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
            +{extra}
          </span>
        )}
      </div>
      {summary && <span className="truncate text-xs text-muted-foreground">{summary}</span>}
    </div>
  );
}

export default EventPeople;
